/**
 * Extract the string type property keys of an object
 */
export type StringKeys<T> = {
  [K in keyof T]: T[K] extends string ? K : never;
}[keyof T];

/**
 *  Type predicates that ensures a variable is not undefined or null.
 */
export const isDefined = <T>(x: T | undefined | null): x is T => {
  return x !== undefined && x !== null;
};

/**
 * Checks that the value is included in the array and type guards it.
 */
export const isIncluded = <T extends any[] | Readonly<any[]>, K>(
  array: T | undefined | null,
  value: K,
): value is T[number] => {
  return array ? array.includes(value as any) : false;
};

/**
 * Returns `x` if it's defined and a non empty string (not trimmed), otherwise `defaultValue` is
 * returned.
 */
export const nonEmptyStringOrDefault = <T extends string, TResult>(
  x: T | undefined | null,
  defaultValue: TResult,
) => (isDefined(x) && x.length > 0 ? x : defaultValue);

/**
 * Type predicate that checks if an array has elements.
 * If expectedNumberOfElements is specified, checks for that exact number; otherwise, checks for more than 0.
 */
export const hasElements = <T>(
  x: T[] | undefined | null,
  expectedNumberOfElements?: number,
): boolean => {
  return (
    isDefined(x) &&
    (isDefined(expectedNumberOfElements) ? x.length === expectedNumberOfElements : x.length > 0)
  );
};

/**
 * Assert that the parameter passed is of type never.
 * Used to create exhaustive case handling for types.
 */
export function assertNever(__: never): never {
  throw new Error(`Error asserting value. Expected never.`);
}

/**
 * Builds up an array of type T based on the boolean value of each tuple.
 * If the boolean is true the value will be included otherwise it will not be included.
 */
export function buildOptionsArray<T>(...args: [boolean, T][]): T[] {
  const array: T[] = [];
  for (const arg of args) {
    if (arg[0]) {
      array.push(arg[1]);
    }
  }
  return array;
}

export function copyMap<T, K>(map: Map<T, K>, options = { deep: false }) {
  return new Map([...map].map((entry) => [entry[0], options.deep ? deepCopy(entry[1]) : entry[1]]));
}

export function mapToArray<TKey, TValue>(map: Map<TKey, TValue>): [TKey, TValue][] {
  return [...map];
}

/**
 * Returns a deep copy of an object.
 * If the value is a primitive the primitive will be returned as is.
 *
 * Warning: Do not pass three objects or similar to this function.
 * It does not handle circular references. To handle this a WeakMap or similar needs to be
 * implemented otherwise it will cause an infinite loop.
 */
export function deepCopy<T>(object: T): T {
  if (typeof object === 'function') {
    throw new Error('Not possible to deep copy objects with functions.');
  }

  if (object instanceof Object) {
    if (Array.isArray(object)) {
      return object.map((item) => deepCopy(item)) as T;
    }

    const copy: any = {};
    Object.keys(object).forEach((key) => {
      copy[key] = deepCopy(object[key as keyof T]);
    });
    return copy;
  }

  return object;
}
